import { useSignal, computed } from "@preact/signals-react";
import React, { useState } from 'react';
import { ErrorField } from "../../../../types/ErrorField";
import SelectTextField, { OptionProps } from "../../../molecules/SelectTextField";
import { getErrorMessage } from "../../../../utils/formErrorUtils";
import { useEffect } from "react";
import MaskEdit from "../../../organisms/MaskEdit";
import { City } from "../../../../hooks/useCities";

import ProfessionAutocomplete from "./ProfessionAutocomplete";
import { NATIONALITIES } from "../../../../constants/nationalities";



interface Props {
    form: any;
    profiles: any;
    errorFields: ErrorField[];
    changeForm: (field: string, value: string) => void;
    provinces: any[];
    cities: any[],
    genders: any[];
    changeProvince: (province: string) => void;
    editForm?: boolean;
    onProfessionChange?: (professionId: string, professionName: string) => void;
}


const IndividualFormAdditionalData: React.FC<Props> = ({ form, profiles, errorFields, changeForm, provinces, cities, genders, changeProvince, editForm = false, onProfessionChange }) => {
    const provincesOpt = useSignal<OptionProps[]>([]);
    const citiesOpt = useSignal<OptionProps[]>([]);
    const isLoadingCities = useSignal(false);
    const [birthdayTitle, setBirthdayTitle] = useState("");
    const [nationalityTitle, setNationalityTitle] = useState("");
    const [maritalStatusTitle, setMaritalStatusTitle] = useState("");
    const [matrimonialRegimeTitle, setMatrimonialRegimeTitle] = useState("");
    const [professionTitle, setProfessionTitle] = useState("");
    const [spouseTitle, setSpouseTitle] = useState("");

    // Lógica de visibilidade dos campos do cônjuge
    const shouldShowSpouseFields = computed(() => {
        return form?.maritalStatus === '2' || form?.maritalStatus === '6';
    });

    const shouldShowSpouseName = computed(() => {
        return shouldShowSpouseFields.value && 
               form?.spouseSocialId && 
               form?.spouseName && 
               form?.spouseName !== 'Ainda não cadastrado';
    });

    const shouldShowSpouseNotFound = computed(() => {
        return shouldShowSpouseFields.value && 
               form?.spouseSocialId && 
               (!form?.spouseName || form?.spouseName === 'Ainda não cadastrado');
    });

    const markRequiredFields = () => {
        let prf = profiles;

        setBirthdayTitle("Data de Nascimento");
        let tmp = ["ISSUER", "GUARANTOR", "CONSENTING_PARTY", "RECIPIENT"];
        if (prf.some((profile: string) => tmp.includes(profile))) {
            setBirthdayTitle("*Data de Nascimento");
        }
        setNationalityTitle("Nacionalidade");
        tmp = ["ISSUER", "GUARANTOR", "RECIPIENT"];
        if (prf.some((profile: string) => tmp.includes(profile))) {
            setNationalityTitle("*Nacionalidade");
        }
        setMaritalStatusTitle("Estado civil");
        tmp = ["ISSUER", "GUARANTOR"];
        if (prf.some((profile: string) => tmp.includes(profile))) {
            setMaritalStatusTitle("*Estado Civil");
        }
        setProfessionTitle("Profissão");
        setSpouseTitle("CPF do Cônjuge");
        setMatrimonialRegimeTitle("Regime de casamento");
        tmp = ["GUARANTOR"];
        if (prf.some((profile: string) => tmp.includes(profile))) {
            setProfessionTitle("*Profissão");
            if (form.maritalStatus === "2" || form.maritalStatus === "6") {
                setMatrimonialRegimeTitle("*Regime de casamento");
                if (form.matrimonialRegimeId === "1" || form.matrimonialRegimeId === "2") {
                    setSpouseTitle("*CPF do Cônjuge");
                }
            }
        }
    }

    useEffect(() => {
        markRequiredFields();
    }, [profiles.value, form.maritalStatus, form.matrimonialRegimeId]);

    useEffect(() => {
        isLoadingCities.value = true;
        citiesOpt.value = cities.map((city: any) => ({
            label: city.name,
            value: city.id,
        }));
        if (form.ibge) {
            const cityId = cities.filter((city: City) => city.ibgeCode === form.ibge);
            changeForm("cityId", cityId.length > 0 ? cityId[0].id : "");
        }
        isLoadingCities.value = false;
    }, [cities]);
    useEffect(() => {
        provincesOpt.value = provinces.map((province: any) => ({
            label: province.name,
            value: province.initials,
        }));

    }, [provinces]);
    return (
        <div className="p-4 m-4">
            <div className="grid grid-cols-12 gap-8">
                <MaskEdit
                    id="birthday"
                    label={birthdayTitle}
                    mask="__/__/____"
                    replacement={{ '_': /\d/ }}
                    className="col-span-3"
                    disabled={form.blockEdit.blockBirthday}
                    value={form.birthday}
                    onChange={(e) => changeForm("birthday", e.target.value.toUpperCase())}
                    errorMessage={getErrorMessage(errorFields, "birthday")}
                />

                <SelectTextField
                    id="gender"
                    name='gender'
                    label="Sexo"
                    value={form.gender}
                    items={genders}
                    className="col-span-3"
                    onChange={(e) => changeForm("gender", e.target.value)}
                    errorMessage={getErrorMessage(errorFields, "gender")}
                />

                <SelectTextField
                    id="nationality"
                    name='nationality'
                    label={nationalityTitle}
                    value={form.nationalityId}
                    disabled={form.blockEdit.blockNationality}
                    items={NATIONALITIES}
                    className="col-span-3"
                    onChange={(e) => changeForm("nationalityId", e.target.value)}
                    errorMessage={getErrorMessage(errorFields, "nationalityId")}
                />

                <ProfessionAutocomplete
                    id="profession"
                    label={professionTitle}
                    value={form.professionId}
                    displayValue={form.professionName || ''}
                    className="col-span-3"
                    onChange={(professionId, professionName) => {
                        changeForm("professionId", professionId);
                        if (onProfessionChange) onProfessionChange(professionId, professionName);
                    }}
                    errorMessage={getErrorMessage(errorFields, "professionId")}
                />

                <SelectTextField
                    id="maritalStatusId"
                    name='maritalStatusId'
                    label={maritalStatusTitle}
                    value={form.maritalStatus}
                    items={[{ "value": "1", "label": "solteiro(a)" }, { "value": "2", "label": "casado(a)" }, { "value": "3", "label": "separado(a) judicialmente" }, { "value": "4", "label": "divorciado(a)" }, { "value": "5", "label": "viúvo(a)" }, { "value": "6", "label": "união estável" }]}
                    className="col-span-3"
                    onChange={(e) => changeForm("maritalStatus", e.target.value)}
                    errorMessage={getErrorMessage(errorFields, "maritalStatusId")}
                />

                <SelectTextField
                    id="matrimonialRegimeId"
                    name='matrimonialRegimeId'
                    label={matrimonialRegimeTitle}
                    value={form.matrimonialRegimeId}
                    items={[{ "value": "1", "label": "casado(a) com comunhão universal de bens" }, { "value": "2", "label": "casado(a) com comunhão parcial de bens" }, { "value": "3", "label": "casado(a) com total separação de bens" }]}
                    className="col-span-3"
                    onChange={(e) => changeForm("matrimonialRegimeId", e.target.value)}
                    errorMessage={getErrorMessage(errorFields, "matrimonialRegimeId")}
                />

                {shouldShowSpouseFields.value && (
                    <MaskEdit
                        id="spouseSocialId"
                        name="spouseSocialId"
                        label={spouseTitle}
                        mask="___.___.___-__"
                        replacement={{ '_': /\d/ }}
                        className="col-span-3"
                        value={form.spouseSocialId}
                        disabled={false}
                        onChange={(e) => changeForm("spouseSocialId", e.target.value)}
                        errorMessage={getErrorMessage(errorFields, "spouseSocialId")}
                    />
                )}
            </div>

            {/* Nome do cônjuge quando encontrado */}
            {shouldShowSpouseName.value && (
                <div className="grid grid-cols-12 gap-8 mt-4">
                    <div className="flex items-center col-span-6">
                        <input 
                            className="block py-2.5 px-0 w-full text-sm text-gray-900 dark:text-gray-100 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 dark:focus:border-blue-400 peer disabled:opacity-50 disabled:cursor-not-allowed dark:text-white" 
                            placeholder=" " 
                            id="name" 
                            disabled 
                            value={form?.spouseName || ''}
                        />
                        <label className="absolute text-sm text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-blue-600 dark:peer-focus:text-blue-400 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6 undefined dark:text-white">
                            Cônjuge
                        </label>
                    </div>
                </div>
            )}

            {/* Status quando cônjuge não encontrado */}
            {shouldShowSpouseNotFound.value && (
                <div className="grid grid-cols-12 gap-8 mt-4">
                    <span 
                        id="spouse-not-found" 
                        className="col-span-6 text-sm text-gray-500 dark:text-gray-400"
                    >
                        Cônjuge não encontrado ou ainda não cadastrado
                    </span>
                </div>
            )}
        </div>
    );
};
export default IndividualFormAdditionalData;
